import React, { useState } from 'react';
import { SignIn } from '@clerk/clerk-react';
import { Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PrivacyPolicyModal } from '../components/modals/PrivacyPolicyModal';
import { TrailerModal } from '../components/modals/TrailerModal';

// Image sources with local fallback pattern
// To use local images, add files to public/assets/decorative/
const IMAGES = {
  evidence: {
    local: '/assets/decorative/evidence-photo.jpg',
    fallback: 'https://images.unsplash.com/photo-1575936123452-b67c3203c357?w=400&h=400&fit=crop',
  },
  justice: {
    local: '/assets/decorative/justice-statue.jpg',
    fallback: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400&h=400&fit=crop',
  },
};

export const AuthPage = () => {
  const [imageErrors, setImageErrors] = useState({});
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);

  const handleImageError = (key, e) => {
    // If local image fails, try fallback; if fallback fails, hide element
    if (e.target.src.includes('/assets/')) {
      e.target.src = IMAGES[key].fallback;
    } else {
      setImageErrors(prev => ({ ...prev, [key]: true }));
    }
  };

  // Shared Clerk appearance config - dark theme matching generating window
  const clerkAppearance = {
    elements: {
      rootBox: 'mx-auto',
      card: 'shadow-none bg-transparent',
      headerTitle: 'text-stone-100',
      headerSubtitle: 'text-stone-300',
      formFieldLabel: 'text-stone-200',
      formFieldInput: 'bg-stone-700/50 border-stone-600 text-stone-100 placeholder:text-stone-400',
      formButtonPrimary: 'bg-amber-600 hover:bg-amber-700',
      footerActionLink: 'text-amber-400 hover:text-amber-300',
      dividerLine: 'bg-stone-600',
      dividerText: 'text-stone-400',
      socialButtonsBlockButton: 'bg-stone-700 border-stone-600 text-stone-200 hover:bg-stone-600',
      socialButtonsBlockButtonText: 'text-stone-200',
      formFieldInputShowPasswordButton: 'text-stone-400 hover:text-stone-200',
      identityPreviewEditButton: 'text-amber-400 hover:text-amber-300',
      formResendCodeLink: 'text-amber-400 hover:text-amber-300',
      otpCodeFieldInput: 'bg-stone-700/50 border-stone-600 text-stone-100',
      footer: 'text-stone-300',
      footerActionText: 'text-stone-300',
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #c19a6b 0%, #b8936f 25%, #c19a6b 50%, #ab8562 75%, #c19a6b 100%)',
        backgroundSize: '400% 400%',
      }}
    >
      {/* Cork texture overlay */}
      <div 
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 2px,
              rgba(139, 90, 43, 0.1) 2px,
              rgba(139, 90, 43, 0.1) 4px
            ),
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 2px,
              rgba(139, 90, 43, 0.1) 2px,
              rgba(139, 90, 43, 0.1) 4px
            )
          `,
          backgroundSize: '20px 20px',
        }}
      />

      {/* Container for decorative items AND content - matching ThemeSelection structure */}
      <div className="relative max-w-6xl w-full">
        {/* Decorative pinned items - matching ThemeSelection sizes and positions */}
        
        {/* Large Evidence Polaroid - Top Left */}
        {!imageErrors.evidence && (
          <div className="absolute -top-24 -left-24 w-56 h-64 bg-white shadow-2xl rotate-[-12deg] transform hover:scale-105 transition-transform duration-300 z-10 hidden md:block">
            <img 
              src={IMAGES.evidence.fallback}
              alt="Evidence"
              className="w-full h-52 object-cover"
              onError={(e) => handleImageError('evidence', e)}
            />
            <div className="p-3 text-center">
              <p className="text-base font-handwriting text-gray-700 font-bold">EVIDENCE #601</p>
            </div>
          </div>
        )}

        {/* Large Yellow Post-it Note - Top Right */}
        <div className="absolute -top-32 -right-20 w-64 h-64 bg-yellow-300 shadow-2xl rotate-[8deg] p-6 transform hover:rotate-[6deg] transition-transform duration-300 z-10 hidden md:block">
          <p className="text-lg font-handwriting text-gray-800 leading-relaxed">
            NEW DETECTIVE?<br/>
            SIGN IN TO<br/>
            ACCESS CASES.<br/>
            🔍
          </p>
        </div>

        {/* Large Red Evidence Folder - Bottom Left */}
        <div className="absolute -bottom-24 -left-20 w-72 h-80 bg-red-100 border-4 border-red-700 shadow-2xl rotate-[-5deg] p-4 transform hover:scale-105 transition-transform duration-300 z-10 hidden md:block">
          <div className="border-4 border-dashed border-red-700 h-full flex flex-col items-center justify-center gap-3">
            <p className="text-2xl font-bold text-red-700 text-center tracking-wider">CASE FILE</p>
            <div className="w-16 h-16 border-4 border-red-700 rounded-full flex items-center justify-center">
              <p className="text-red-700 font-bold text-xs text-center">DO NOT<br/>REMOVE</p>
            </div>
            <p className="text-sm font-bold text-red-700">CLASSIFIED</p>
          </div>
        </div>

        {/* Large Clue Polaroid - Bottom Right */}
        {!imageErrors.justice && (
          <div className="absolute -bottom-20 -right-24 w-52 h-60 bg-white shadow-2xl rotate-[10deg] transform hover:scale-105 transition-transform duration-300 z-10 hidden md:block">
            <img 
              src={IMAGES.justice.fallback}
              alt="Clue"
              className="w-full h-48 object-cover"
              onError={(e) => handleImageError('justice', e)}
            />
            <div className="p-2 text-center">
              <p className="text-base font-handwriting text-gray-700 font-bold">Check Timelines</p>
            </div>
          </div>
        )}

        {/* Large Pink Post-it Note - Left Side */}
        <div className="absolute top-1/4 -left-28 w-56 h-56 bg-pink-200 shadow-2xl rotate-[-15deg] p-5 transform hover:rotate-[-12deg] transition-transform duration-300 z-10 hidden lg:block">
          <p className="text-base font-handwriting text-gray-800 leading-relaxed">
            SOLVE CRIMES<br/>
            FIND CLUES<br/>
            CATCH KILLERS!!
          </p>
        </div>

        {/* Confidential Files Folder - Right Side */}
        <div className="absolute bottom-1/3 -right-20 w-60 h-48 bg-yellow-100 shadow-2xl rotate-[12deg] transform hover:rotate-[10deg] transition-transform duration-300 z-10 hidden lg:block overflow-hidden border-2 border-amber-700">
          <div className="bg-amber-700 text-white text-center py-2 text-xs font-bold tracking-widest">CONFIDENTIAL FILES</div>
          <div className="p-4 space-y-1">
            <div className="h-2 bg-amber-700/30 w-full"></div>
            <div className="h-2 bg-amber-700/30 w-5/6"></div>
            <div className="h-2 bg-amber-700/30 w-4/5"></div>
            <div className="h-2 bg-amber-700/30 w-full"></div>
          </div>
        </div>

        {/* Push Pins scattered */}
        <div className="absolute top-10 left-1/4 w-6 h-6 rounded-full bg-red-600 shadow-lg z-20 hidden md:block" />
        <div className="absolute top-20 right-1/3 w-5 h-5 rounded-full bg-blue-500 shadow-lg z-20 hidden md:block" />
        <div className="absolute bottom-32 left-1/3 w-6 h-6 rounded-full bg-yellow-400 shadow-lg z-20 hidden md:block" />

        {/* Main Content - inside same container as decoratives */}
        <div className="text-center mb-6 space-y-4 relative z-20">
          <h1 
            className="text-7xl md:text-8xl font-bold tracking-wider text-gray-900 drop-shadow-[0_4px_4px_rgba(255,255,255,0.3)]"
            style={{ fontFamily: 'Playfair Display, serif', textShadow: '2px 2px 4px rgba(255,255,255,0.5)' }}
          >
            MysHaps
          </h1>
          <p className="text-2xl text-gray-700 tracking-wide drop-shadow-sm font-light italic">
            Mystery Happens, Inc.
          </p>
        </div>

        {/* Trailer - secondary to sign-in */}
        <div className="flex justify-center mb-6 relative z-20">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowTrailer(true)}
            className="border-gray-800/40 bg-white/20 text-gray-900 hover:bg-white/40 hover:text-gray-900"
          >
            <Play aria-hidden="true" />
            Watch the trailer
          </Button>
        </div>

        {/* Clerk Sign In Component - Dark style matching generating window */}
        <div className="flex justify-center relative z-20">
          <div className="bg-stone-800/95 backdrop-blur-sm rounded-xl shadow-2xl p-4 border border-stone-700/50">
            <SignIn
              appearance={clerkAppearance}
              routing="path"
              path="/sign-in"
              forceRedirectUrl="/"
            />
          </div>
        </div>

        {/* Privacy Policy Link */}
        <div className="mt-4 text-center relative z-20">
          <button
            onClick={() => setShowPrivacyPolicy(true)}
            className="text-gray-700 hover:text-gray-900 text-sm underline underline-offset-2 transition-colors"
          >
            Privacy Policy
          </button>
        </div>

        {/* Footer note */}
        <p className="mt-4 text-gray-800 text-sm drop-shadow-sm font-medium text-center relative z-20">
          An immersive murder mystery experience
        </p>
      </div>

      <TrailerModal open={showTrailer} onOpenChange={setShowTrailer} />

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
      />
    </div>
  );
};
